/**
 * API client for admin endpoints. Wraps fetch with the Identity JWT and
 * surfaces errors as thrown Errors (so React Query / try-catch can handle).
 */
import { getJwt } from './auth';
import type {
  Fund, NewsItem, NewsListResponse, NewsStatus,
  Founder, FoundersListResponse, DashboardStats, StatsRange,
  AuditAction, AuditListResponse,
  Programme, ProgrammesListResponse,
  Finding, FindingsListResponse, PromoteFindingResponse,
  FounderSubmission, FounderSubmissionsListResponse,
  Lead, LeadsListResponse,
} from '../types';

// Netlify Functions v2 with `config.path` register the function ONLY at the
// declared path, not at the legacy `/.netlify/functions/<name>` URL. Use the
// canonical paths declared in netlify/functions/admin-*.mjs.
const BASE = '/api/admin';

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const jwt = await getJwt();
  if (!jwt) throw new Error('Not authenticated');
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${jwt}`);
  if (!headers.has('Content-Type') && init?.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    // Surface a tidy error message rather than dumping the entire response
    // body (which is often the HTML 404 page when a misconfigured redirect
    // intercepts the request). Prefer JSON {error} when present, fall back
    // to truncated text.
    let detail = '';
    try {
      const ct = res.headers.get('content-type') ?? '';
      const text = await res.text();
      if (ct.includes('application/json')) {
        try { detail = JSON.parse(text).error ?? text; } catch { detail = text; }
      } else {
        detail = text;
      }
    } catch { /* ignore */ }
    if (detail.length > 200) detail = detail.slice(0, 200) + '…';
    throw new Error(`${res.status} ${res.statusText}${detail ? `: ${detail}` : ''}`);
  }
  return res;
}

/* ---------------- Funds ---------------- */

export interface FundsListResponse {
  ok: true;
  funds: Fund[];
  count: number;
  hasUnpublishedChanges?: boolean;
}

export async function listFunds(): Promise<FundsListResponse> {
  const res = await authedFetch('/funds');
  return res.json();
}

export async function updateFund(id: string, fields: Partial<Fund>): Promise<{ ok: true; fund: Fund }> {
  const res = await authedFetch(`/funds?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields }),
  });
  return res.json();
}

export async function createFund(fields: Partial<Fund>): Promise<{ ok: true; fund: Fund }> {
  const res = await authedFetch('/funds', {
    method: 'POST',
    body: JSON.stringify({ fields }),
  });
  return res.json();
}

export async function deleteFund(id: string): Promise<{ ok: true }> {
  const res = await authedFetch(`/funds?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return res.json();
}

/* ---------------- Publish ---------------- */

export interface PublishResponse {
  ok: true;
  buildId?: string;
  triggeredAt: string;
}

export async function triggerPublish(): Promise<PublishResponse> {
  const res = await authedFetch('/publish', { method: 'POST' });
  return res.json();
}

/* ---------------- News ---------------- */

export async function listNews(status?: NewsStatus | 'all'): Promise<NewsListResponse> {
  const qs = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const res = await authedFetch(`/news${qs}`);
  return res.json();
}

export async function updateNews(id: string, fields: Partial<NewsItem>): Promise<{ ok: true; item: NewsItem }> {
  const res = await authedFetch(`/news?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields }),
  });
  return res.json();
}

export async function approveNews(id: string): Promise<{ ok: true; item: NewsItem }> {
  return updateNews(id, { status: 'published' });
}

export async function rejectNews(id: string): Promise<{ ok: true; item: NewsItem }> {
  return updateNews(id, { status: 'rejected' });
}

export async function deleteNews(id: string): Promise<{ ok: true }> {
  const res = await authedFetch(`/news?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return res.json();
}

/* ---------------- Founders ---------------- */

export async function listFounders(): Promise<FoundersListResponse> {
  const res = await authedFetch('/founders');
  return res.json();
}

export async function getFounder(id: string): Promise<{ ok: true; item: Founder }> {
  const res = await authedFetch(`/founders?id=${encodeURIComponent(id)}`);
  return res.json();
}

/* ---------------- Dashboard stats ---------------- */

export async function getDashboardStats(range: StatsRange = '30d'): Promise<DashboardStats> {
  const res = await authedFetch(`/stats?range=${range}`);
  return res.json();
}

/* ---------------- Settings ---------------- */

export interface AdminSettings {
  ok: true;
  user: { email: string; name: string | null };
  admins: { emails: string[]; total: number };
  env: {
    airtable: boolean;
    identity: boolean;
    buildHook: boolean;
    slack: boolean;
    anthropic: boolean;
    restoreSecret: boolean;
    resend: boolean;
  };
  tables: { funds: string; news: string; founders: string };
  site: { primary: string; admin: string };
}

export async function getSettings(): Promise<AdminSettings> {
  const res = await authedFetch('/settings');
  return res.json();
}

/* ---------------- Activity log ---------------- */

export interface ListActivityOpts {
  table?: string;
  action?: AuditAction;
  limit?: number;
  offset?: number;
}

export async function listActivity(opts: ListActivityOpts = {}): Promise<AuditListResponse> {
  const qs = new URLSearchParams();
  if (opts.table) qs.set('table', opts.table);
  if (opts.action) qs.set('action', opts.action);
  if (typeof opts.limit === 'number') qs.set('limit', String(opts.limit));
  if (typeof opts.offset === 'number') qs.set('offset', String(opts.offset));
  const qsStr = qs.toString();
  const res = await authedFetch(`/activity${qsStr ? `?${qsStr}` : ''}`);
  return res.json();
}

/* ---------------- Programmes non-dilutifs ---------------- */

export async function listProgrammes(): Promise<ProgrammesListResponse> {
  const res = await authedFetch('/programmes');
  return res.json();
}

export async function getProgramme(id: string): Promise<{ ok: true; programme: Programme }> {
  const res = await authedFetch(`/programmes?id=${encodeURIComponent(id)}`);
  return res.json();
}

export async function updateProgramme(id: string, fields: Partial<Programme>): Promise<{ ok: true; programme: Programme }> {
  const res = await authedFetch(`/programmes?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields }),
  });
  return res.json();
}

export async function createProgramme(fields: Partial<Programme>): Promise<{ ok: true; programme: Programme }> {
  const res = await authedFetch('/programmes', {
    method: 'POST',
    body: JSON.stringify({ fields }),
  });
  return res.json();
}

export async function deleteProgramme(id: string): Promise<{ ok: true }> {
  const res = await authedFetch(`/programmes?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return res.json();
}

/* ---------------- Findings (veille) ---------------- */

export async function listFindings(): Promise<FindingsListResponse> {
  const res = await authedFetch('/findings');
  return res.json();
}

export async function getFinding(id: string): Promise<{ ok: true; finding: Finding }> {
  const res = await authedFetch(`/findings?id=${encodeURIComponent(id)}`);
  return res.json();
}

export async function updateFinding(id: string, fields: Partial<Finding>): Promise<{ ok: true; finding: Finding }> {
  const res = await authedFetch(`/findings?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields }),
  });
  return res.json();
}

export async function promoteFinding(
  id: string,
  overrides?: Record<string, unknown>
): Promise<PromoteFindingResponse> {
  const res = await authedFetch(`/findings?action=promote&id=${encodeURIComponent(id)}`, {
    method: 'POST',
    body: JSON.stringify({ overrides: overrides || {} }),
  });
  return res.json();
}

export async function deleteFinding(id: string): Promise<{ ok: true }> {
  const res = await authedFetch(`/findings?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return res.json();
}

/* ---------------- Founder submissions (inbox) ---------------- */

export async function listFounderSubmissions(): Promise<FounderSubmissionsListResponse> {
  const res = await authedFetch('/founder-submissions');
  return res.json();
}

export async function updateFounderSubmission(id: string, fields: Partial<FounderSubmission>): Promise<{ ok: true; submission: FounderSubmission }> {
  const res = await authedFetch(`/founder-submissions?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields }),
  });
  return res.json();
}

export async function deleteFounderSubmission(id: string): Promise<{ ok: true }> {
  const res = await authedFetch(`/founder-submissions?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return res.json();
}

/* ---------------- Leads ---------------- */

export async function listLeads(): Promise<LeadsListResponse> {
  const res = await authedFetch('/leads');
  return res.json();
}

export async function updateLead(id: string, fields: Partial<Lead>): Promise<{ ok: true; lead: Lead }> {
  const res = await authedFetch(`/leads?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields }),
  });
  return res.json();
}

export async function deleteLead(id: string): Promise<{ ok: true }> {
  const res = await authedFetch(`/leads?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return res.json();
}
